<template>
  <div class="process-steps-container">
    <div class="process-steps-overlay">
      <div class="steps-content">
        <div class="steps-header">
          <div class="steps-title">Processing Status</div>
          <div class="steps-subtitle">
            <span class="typing-text">{{ currentStepText }}</span>
            <span class="dots">
              <span>.</span><span>.</span><span>.</span>
            </span>
          </div>
        </div>
        <div class="steps-timeline">
          <div v-for="(step, index) in processSteps" :key="step.id" 
               class="timeline-item"
               :class="{ 
                 'completed': step.status === 'completed',
                 'current': step.status === 'current',
                 'last': index === processSteps.length - 1
               }">
            <div class="timeline-node">
              <div class="node-icon">
                <div class="node-border"></div>
                <div class="node-inner">
                  <i v-if="step.status === 'completed'" class="el-icon-check"></i>
                  <template v-else-if="step.status === 'current'">
                    <svg class="circular-progress" viewBox="0 0 50 50">
                      <circle class="path" cx="25" cy="25" r="20" fill="none" stroke-width="4"></circle>
                    </svg>
                  </template>
                  <span v-else class="step-number">{{ index + 1 }}</span>
                </div>
              </div>
              <div class="node-content">
                <div class="step-text">{{ step.text }}</div>
                <div class="step-progress" v-if="step.status === 'current'">
                  <div class="progress-bar">
                    <div class="progress-fill">
                      <div class="progress-glow"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div class="timeline-line" v-if="!step.last">
              <div class="line-progress" :class="{ 'active': step.status === 'completed' }"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProcessSteps',
  props: {
    // 当前步骤信息数组
    processSteps: {
      type: Array,
      required: true,
      default: () => []
    },
    // 当前步骤的描述文本
    currentStepText: {
      type: String,
      required: false,
      default: 'Processing...'
    }
  },
  data() {
    return {
      // 用于实时更新当前步骤的处理时间
      currentTimer: null,
      currentStepStartTime: null,
      realTimeElapsed: 0
    }
  },
  methods: {
    formatTime(ms) {
      if (!ms) return '';
      const seconds = Math.floor(ms / 1000);
      if (seconds < 60) {
        return `${seconds}s`;
      }
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = seconds % 60;
      return `${minutes}m ${remainingSeconds}s`;
    },
    // 开始计时
    startTimer() {
      this.currentStepStartTime = Date.now();
      this.realTimeElapsed = 0;
      
      // 清除之前的定时器
      if (this.currentTimer) {
        clearInterval(this.currentTimer);
      }
      
      // 每秒更新一次时间
      this.currentTimer = setInterval(() => {
        if (this.currentStepStartTime) {
          this.realTimeElapsed = Date.now() - this.currentStepStartTime;
        }
      }, 1000);
    },
    // 停止计时
    stopTimer() {
      if (this.currentTimer) {
        clearInterval(this.currentTimer);
        this.currentTimer = null;
      }
      this.currentStepStartTime = null;
      this.realTimeElapsed = 0;
    }
  },
  watch: {
    processSteps: {
      immediate: true,
      handler(newSteps) {
        // 找到当前正在进行的步骤
        const currentStep = newSteps.find(step => step.status === 'current');
        
        if (currentStep) {
          // 如果有正在进行的步骤，开始计时
          this.startTimer();
        } else {
          // 如果没有正在进行的步骤，停止计时
          this.stopTimer();
        }
      }
    }
  },
  computed: {
    // 计算当前步骤的实时处理时间
    currentStepTime() {
      return this.realTimeElapsed;
    }
  },
  beforeDestroy() {
    // 组件销毁前清理定时器
    this.stopTimer();
  }
}
</script>

<style scoped>
.process-steps-container {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.process-steps-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: overlay-fade-in 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.steps-content {
  max-width: 580px;
  width: 100%;
  padding: 40px;
  background: linear-gradient(145deg, #ffffff, #fafbfc);
  border-radius: 20px;
  box-shadow: 
    0 8px 30px rgba(0, 0, 0, 0.06),
    0 0 0 1px rgba(0, 0, 0, 0.01);
  animation: content-slide-up 0.6s cubic-bezier(0.16, 1, 0.3, 1);
}

.steps-header {
  text-align: center;
  margin-bottom: 50px;
}

.steps-title {
  font-size: 28px;
  font-weight: 600;
  letter-spacing: -0.3px;
  background: linear-gradient(135deg, #4b6cb7, #6e8cd7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 10px;
}

.steps-subtitle {
  font-size: 15px;
  color: #8492a6;
  display: flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.2px;
}

.steps-timeline {
  position: relative;
  padding: 16px 0;
}

.timeline-item {
  position: relative;
  padding-left: 50px;
  margin-bottom: 40px;
}

.timeline-item:last-child {
  margin-bottom: 0;
}

.timeline-node {
  display: flex;
  align-items: flex-start;
  position: relative;
}

.node-icon {
  position: absolute;
  left: -50px;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2;
}

.node-border {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(75, 108, 183, 0.15), rgba(110, 140, 215, 0.15));
  animation: border-pulse 2s infinite;
}

.node-inner {
  position: relative;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 
    0 3px 8px rgba(0, 0, 0, 0.06),
    0 0 0 2px rgba(255, 255, 255, 0.95);
  transition: all 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.completed .node-inner {
  background: #6e8cd7;
  color: white;
  transform: scale(1.05);
}

.current .node-inner {
  background: #4b6cb7;
  color: white;
  animation: node-pulse 2s infinite;
}

.timeline-line {
  position: absolute;
  top: 36px;
  left: 18px;
  bottom: -40px;
  width: 1px;
  background: rgba(0, 0, 0, 0.04);
  z-index: 1;
}

.line-progress {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 0;
  background: linear-gradient(to bottom, #4b6cb7, #6e8cd7);
  transition: height 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.line-progress.active {
  height: 100%;
}

.node-content {
  padding-left: 20px;
  flex-grow: 1;
}

.step-text {
  font-size: 15px;
  color: #3c4858;
  margin-bottom: 6px;
  transition: all 0.3s ease;
  opacity: 0.9;
  letter-spacing: 0.1px;
  line-height: 1.4;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.completion-time {
  font-size: 13px;
  color: #8492a6;
  opacity: 0.8;
  font-variant-numeric: tabular-nums;
}

.completed .step-text {
  color: #4b6cb7;
  opacity: 0.9;
}

.current .step-text {
  color: #4b6cb7;
  font-weight: 500;
  opacity: 1;
}

.circular-progress {
  width: 20px;
  height: 20px;
  animation: rotate 2s linear infinite;
}

.circular-progress .path {
  stroke: #fff;
  stroke-linecap: round;
  animation: dash 1.5s ease-in-out infinite;
}

.step-progress {
  height: 2px;
  margin-top: 10px;
  border-radius: 1px;
  overflow: hidden;
  opacity: 0.8;
}

.progress-bar {
  height: 100%;
  background: rgba(75, 108, 183, 0.08);
  border-radius: 1px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4b6cb7, #6e8cd7);
  border-radius: 1px;
  position: relative;
  animation: progress-animation 2s ease-in-out infinite;
}

.progress-glow {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
  animation: glow-animation 2s linear infinite;
}

@keyframes border-pulse {
  0% {
    transform: scale(1);
    opacity: 0.15;
  }
  50% {
    transform: scale(1.08);
    opacity: 0.2;
  }
  100% {
    transform: scale(1);
    opacity: 0.15;
  }
}

@keyframes node-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(75, 108, 183, 0.3);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(75, 108, 183, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(75, 108, 183, 0);
  }
}

@keyframes overlay-fade-in {
  from { 
    opacity: 0;
    backdrop-filter: blur(0);
  }
  to { 
    opacity: 1;
    backdrop-filter: blur(12px);
  }
}

@keyframes content-slide-up {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes dot-fade {
  0%, 100% { opacity: 0; }
  50% { opacity: 1; }
}

@keyframes rotate {
  100% { transform: rotate(360deg); }
}

@keyframes dash {
  0% {
    stroke-dasharray: 1, 150;
    stroke-dashoffset: 0;
  }
  50% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -35;
  }
  100% {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: -124;
  }
}

@keyframes progress-animation {
  0% {
    width: 0;
    opacity: 0.8;
  }
  50% {
    width: 100%;
    opacity: 1;
  }
  100% {
    width: 0;
    opacity: 0.8;
  }
}

@keyframes glow-animation {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}
</style> 